import { useMutation } from '@tanstack/react-query';
import { Loader2, Trash2 } from 'lucide-react';
import type { ComponentProps, FC, ReactNode } from 'react';
import { useState } from 'react';
import { useListContext, useNotify, useTranslate } from 'ra-core';
import { Confirm } from '../../components/admin/confirm';
import { Button } from '../../components/ui/button';

type CustomBulkActionProps = {
  mutationFn: (ids: any[]) => Promise<any>;
  /**
   * Whether a dialog is presented asking the user to confirm before they can proceed
   */
  requireConfirmation?: boolean;
  /** Translation key or text of the button */
  label: string;
  buttonIcon?: ReactNode;
  variant?: ComponentProps<typeof Button>['variant'];
  modalContent?: string;
  modalTitle?: string;
  successMessage?: string;
  errorMessage?: string;
};

/**
 * Create bulk actions for resources that require custom mutation functions. These are usually resources which don't have a primary key (ID), or which have bulk operations beyond the standard "Delete" operation.
 */
export const CustomBulkAction: FC<CustomBulkActionProps> = (props) => {
  const {
    mutationFn,
    label,
    variant = 'ghost',
    buttonIcon = <Trash2 />,
    modalContent = 'ra.message.bulk_delete_content',
    modalTitle = 'ra.message.bulk_delete_title',
    successMessage = 'ra.notification.deleted',
    errorMessage = 'ra.notification.http_error',
  } = props;

  const { selectedIds, refetch, onUnselectItems } = useListContext();

  const [isOpen, setIsOpen] = useState(false);
  const notify = useNotify();
  const translate = useTranslate();

  const bulkMutate = useMutation({
    mutationFn,
    onSuccess: (_data, variables) => {
      notify(successMessage, {
        type: 'info',
        messageArgs: { count: variables.length },
      });
    },
    onError: () => {
      notify(errorMessage, {
        type: 'error',
        autoHideDuration: 5000,
      });
    },
    onSettled: () => {
      setIsOpen(false);
      void refetch();
      onUnselectItems();
    },
  });

  // Our `bulk_delete_*` messages (`englishTranslations.ts`) only use the count
  const translateOptions = { count: selectedIds.length };

  return (
    <>
      <Button
        type="button"
        variant={variant}
        disabled={bulkMutate.isPending}
        onClick={() => {
          if (props.requireConfirmation) {
            setIsOpen(true);
          } else {
            bulkMutate.mutate(selectedIds);
          }
        }}
      >
        {bulkMutate.isPending ? (
          <Loader2 className="animate-spin" aria-hidden />
        ) : (
          buttonIcon
        )}
        {translate(label, { _: label })}
      </Button>
      <Confirm
        isOpen={isOpen}
        loading={bulkMutate.isPending}
        title={modalTitle}
        titleTranslateOptions={translateOptions}
        content={modalContent}
        contentTranslateOptions={translateOptions}
        onConfirm={() => bulkMutate.mutate(selectedIds)}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
};
